import { useState, useEffect, useMemo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { APIGenerateExplainer } from "../helper/APIFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faTimes, faDownload, faCrown, faCopy } from "@fortawesome/free-solid-svg-icons";
import ShareModal from "../components/VisualModals/Share";
import { UserInfo } from "../helper/Context";
import { prepBrandProfileList } from "../helper/Utils";
import { ExplainerDisclaimers } from "../helper/ExplainerDisclaimers";
import "./GenerateExplainer.css";
import ReportInaccuraciesButton from "../components/ReportInaccuraciesButton";
import { ELEVENLABS_LANGUAGE_MAP } from "../helper/Language_Utils";
import VoiceSelector from "../components/VoiceSelector";
import { getVideoOptions, ExplainerRecorder } from "../components/ExplainerRecorder";
const DEFAULT_MODEL = "eleven_v2";

// Language codes supported by ElevenLabs
const LANGUAGE_CODES = ELEVENLABS_LANGUAGE_MAP;

const explainerAudiences = {
  "general_public": { "name": "General Public", "description": "The general public is the audience for this explainer." },
  "patients": { "name": "Patients", "description": "Patients are the audience for this explainer. This includes people who are patients, or concerned friends and family members of the disease or condition being explained, ." },
  "undergraduates": { "name": "Undergraduate Students", "description": "Undergraduate students are the audience for this explainer. This includes people who are undergraduate students in the relevant field, or a related field, with a strong understanding of the basics, but limited specialist knowledge." },
  // "field_experts": {"name": "Field Experts", "description": "Field experts are the audience for this explainer. This includes people who have or are pursuing a postgraduate degree in the relevant field, or eqivalent knowledge."},
  "children": { "name": "Children", "description": "Children are the audience for this explainer. This includes those under the age of 13, with little to no technical knowledge, but good understanding of the language used." },
  "teenagers": { "name": "Teenagers", "description": "Teenagers are the audience for this explainer. That includes those between the ages of 13 and 18, with some basic technical knowledge, but still need an explanation that is easy to understand." },
  "amatuers": { "name": "Intreseted Amateurs", "description": "Intreseted amateurs are the audience for this explainer. This includes people who are interested in the disease or condition being explained, but are not experts in the field. They may lack formal training and depth of knowledge in the relevant field, but are interested in the techincal details and explanations." },
}
const musicOptions = [{
  "name": "Lucid",
  "url": "https://vis.snorkle.io/resources/explainer/music/lucid.mp3"
},
{
  "name": "Particle",
  "url": "https://vis.snorkle.io/resources/explainer/music/particle.mp3"
}
]

const refocusOnIframe = (target) => {
  target?.blur?.();
  document.getElementById("gen-explainer-iframe-id")?.contentWindow?.focus?.();
}

const getGenerationStatusMessage = (status, audio) => {
  const totalSteps = audio ? 5 : 4;
  switch (status || "") {
    // case "query_cleanup": //Not sure if we want to indicate this step in the UI
    //   return "Getting ready (1/${totalSteps})...";  
    case "initial_draft":
      return `Generating initial draft (2/${totalSteps})...`;
    case "proofreading":
      return `Proofreading explainer (3/${totalSteps})...`;
    case "dubbing":
      return `Generating audio (4/${totalSteps})...`;
    case "finalizing":
      return `Finalizing explainer (${totalSteps}/${totalSteps})...`;
    case "":
    default:
      return `Getting ready (1/${totalSteps})...`;
  }
}

export default function GenExplainer() {
  const location = useLocation();
  let savedAudience = localStorage.getItem('explainerAudience');
  if (!savedAudience || !Object.keys(explainerAudiences).includes(savedAudience)) {
    localStorage.setItem('explainerAudience', "general_public");
    savedAudience = "general_public";
  }
  // Basic states
  const [query, setQuery] = useState("");
  const [explainer, setExplainer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [disclaimer, setDisclaimer] = useState(ExplainerDisclaimers["en"]);
  const [disclaimerTouched, setDisclaimerTouched] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(localStorage.getItem('explainerDisclaimerEnabled') === 'false' ? false : true);
  const [selectedAudience, setSelectedAudience] = useState(savedAudience);
  const { userInfo } = useContext(UserInfo);
  const [brandingProfileId, setBrandingProfileId] = useState(null);
  const [brandingProfileList, setBrandingProfileList] = useState([]);
  useEffect(() => {
    if (userInfo?.branding_profiles) {
      setBrandingProfileId(prepBrandProfileList(userInfo?.branding_profiles)?.[0]?.id);
      let list = [{ "id": "", "profile_name": "None" }];
      list = list.concat(prepBrandProfileList(userInfo?.branding_profiles, "organization").map(e => ({ ...e, suffix: "(Organization)" })));
      list = list.concat(prepBrandProfileList(userInfo?.branding_profiles, "personal").map(e => ({ ...e, suffix: "(Personal)" })));
      list = list.filter(e => e.id || e.profile_name == "None");
      setBrandingProfileList(list);
    }
  }, [userInfo]);
  const [currentGenerationStatus, setCurrentGenerationStatus] = useState("");
  const [recordChoice, setRecordChoice] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [backgroundMusic, setBackgroundMusic] = useState("");
  // Prepopulate query from navigation state (from dashboard)
  useEffect(() => {
    if (location.state?.prompt) {
      setQuery(location.state.prompt);
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedAudience && Object.keys(explainerAudiences).includes(selectedAudience)) {
      localStorage.setItem('explainerAudience', selectedAudience);
    }
    if (selectedAudience == "patients") {
      setShowDisclaimer(true);
    }
  }, [selectedAudience]);

  // Audio settings
  const [enableAudio, setEnableAudio] = useState(localStorage.getItem('explainerAudioEnabled') === 'false' ? false : true);
  const [selectedVoice, setSelectedVoice] = useState('21m00Tcm4TlvDq8ikWAM');
  const [voices] = useState([]);
  const [playbackRate] = useState(0.8);
  const [genderFilter] = useState('all');
  const [languageCode, setLanguageCode] = useState('');
  const [voiceSettings] = useState({
    stability: 0.5,
    similarity_boost: 0.75,
  });

  const getVoiceSample = (voice, lang) => {
    lang = lang || "en";
    return voice?.language?.[lang]?.sample || voice?.language?.en?.sample;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sampleAudio = useMemo(() => getVoiceSample(voices.find(v => v.voice_id === selectedVoice), languageCode), [voices, selectedVoice, languageCode]);

  const getFilteredVoices = () => {
    let filtered = [...voices];
    if (genderFilter !== 'all') {
      filtered = filtered.filter(voice => voice.gender === genderFilter);
    }
    return filtered;
  };

  useEffect(() => {
    const filteredVoices = getFilteredVoices();
    if (filteredVoices.length > 0 && !filteredVoices.find(v => v.voice_id === selectedVoice)) {
      setSelectedVoice(filteredVoices[0].voice_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode, genderFilter, selectedVoice]);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('explainerDisclaimerEnabled', showDisclaimer ? 'true' : 'false');
  }, [showDisclaimer]);
  useEffect(() => {
    localStorage.setItem('explainerAudioEnabled', enableAudio ? 'true' : 'false');
  }, [enableAudio]);

  // Download settings
  const [downloadResolution, setDownloadResolution] = useState('720p');
  const [isDownloading, setIsDownloading] = useState(false);

  // Copy to clipboard state
  const [linkCopied, setLinkCopied] = useState(false);

  // Get available languages
  const getAvailableLanguages = () => {
    return Object.keys(LANGUAGE_CODES).sort((a, b) => LANGUAGE_CODES[a].localeCompare(LANGUAGE_CODES[b]));
  };


  // Lock body scroll while generating
  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isLoading]);

  const handleGenerateExplainer = () => {
    if (!query.trim()) {
      setError('Please enter a topic or question for your explainer');
      return;
    }

    setIsLoading(true);
    setError("");
    setExplainer(null);

    const payload = {
      query: query,
      audio_enabled: enableAudio,
    };
    if (brandingProfileId) {
      payload.branding_profile = brandingProfileId;
    }
    if (languageCode) {
      payload.language_code = languageCode;
    }

    // Add audio parameters if audio is enabled
    if (enableAudio) {
      payload.voice_id = selectedVoice;
      payload.model_id = "eleven_v3"
      payload.voice_settings = voiceSettings;
      payload.playback_rate = playbackRate;
    }
    if (showDisclaimer) {
      payload.secondary_text = disclaimer;
    }
    payload.audience = selectedAudience || "general_public";
    payload.music = backgroundMusic || "";
    setCurrentGenerationStatus(getGenerationStatusMessage("", enableAudio));
    APIGenerateExplainer(
      payload,
      (explainerResponse) => {
        setExplainer(explainerResponse);
        setIsLoading(false);
        setFeedback("");
        setCurrentGenerationStatus("");
      },
      (error) => {
        setError(typeof error === 'string' ? error : 'Failed to generate explainer. Please try again.');
        setIsLoading(false);
      },
      (progress) => {
        setCurrentGenerationStatus(getGenerationStatusMessage(progress.current_step || "", enableAudio));
      }
    );
  };

  const handleSubmitFeedback = () => {
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }

    // TODO: This will be implemented when backend supports explainer modifications
    alert('Explainer modification feature coming soon! Your feedback: ' + feedback);
    // In the future, this would call an API to regenerate the explainer with the feedback
  };

  const handleNewExplainer = () => {
    setExplainer(null);
    setQuery("");
    setFeedback("");
    setError("");
  };

  const handleCloseExplainer = () => {
    setExplainer(null);
  };

  const handleDownloadVideo = () => {
    // TODO: Implement actual download functionality when backend supports it
    setIsDownloading(true);

    // Check if premium resolution is selected
    const premiumResolutions = ['1080p', '4k'];
    const isPremium = premiumResolutions.includes(downloadResolution);

    if (isPremium) {
      alert(`${downloadResolution} download is a premium feature. Please upgrade your account to access high-resolution downloads.`);
      setIsDownloading(false);
      return;
    }

    // Simulate download process
    setTimeout(() => {
      alert(`Download feature coming soon! Selected resolution: ${downloadResolution}`);
      setIsDownloading(false);
    }, 1000);
  };

  const handleCopyLink = () => {
    if (explainer && explainer.url) {
      navigator.clipboard.writeText(explainer.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };
  useEffect(() => {
    if (!disclaimerTouched && languageCode) {
      setDisclaimer(ExplainerDisclaimers[languageCode]);
    }
  }, [disclaimerTouched, languageCode]);

  return (
    <div className="gen-explainer-page">
      {/* Full-screen overlay while generating */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(10, 8, 30, 0.75)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.25rem',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            border: '4px solid rgba(139, 92, 246, 0.25)',
            borderTop: '4px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'gen-explainer-spin 0.9s linear infinite',
          }} />
          <p style={{ color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>
            Generating your explainer…
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
            This can take a minute. Please don't navigate away.
            {currentGenerationStatus && (
              <div className="gen-explainer-loading-submessage">
                {currentGenerationStatus}
              </div>
            )}
          </p>
        </div>
      )}

      <div className="gen-explainer-container">
        {/* Header */}
        <div className="gen-explainer-header">
          <h1>Generate Explainer Video Content</h1>
          <p>Create 3D biomedical explainer videos with AI-powered narration and visualization</p>
          <div style={{ marginTop: '12px' }}>
            <ReportInaccuraciesButton context="Explainer Video Generator" variant="text" />
          </div>
        </div>

        {/* Main Configuration Card */}
        {!explainer && (
          <div className="gen-explainer-card">
            {/* Topic/Query Input */}
            <div className="gen-explainer-form-group">
              <label htmlFor="explainer-query" className="gen-explainer-label">
                Description of Video
                <span className="gen-explainer-label-description">
                  We currently support medical and biomedical topics.
                </span>
              </label>
              <textarea
                id="explainer-query"
                className="gen-explainer-textarea"
                placeholder="e.g., 'Show how the digestive system works', 'How do beta blockers affect the heart?', or 'Explain the overall structure of the brain'"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Language Selection Only */}
            <div className="gen-explainer-form-group">
              <label htmlFor="language-select" className="gen-explainer-label">
                Language (Optional)
              </label>
              <select
                id="language-select"
                className="gen-explainer-select"
                value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Auto-detect</option>
                {getAvailableLanguages().map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGE_CODES[code]}
                  </option>
                ))}
              </select>
            </div>

            {/* Audio Enable/Disable */}
            <div className="gen-explainer-form-group">
              <div className="gen-explainer-checkbox-container" onClick={() => setEnableAudio(!enableAudio)}>
                <input
                  type="checkbox"
                  id="enable-audio"
                  className="gen-explainer-checkbox"
                  checked={enableAudio}
                  onChange={(e) => setEnableAudio(e.target.checked)}
                  disabled={isLoading}
                />
                <label htmlFor="enable-audio" className="gen-explainer-checkbox-label">
                  Enable Audio Narration
                </label>
              </div>
            </div>

            {/* Audio Settings (shown only when audio is enabled) */}
            {enableAudio && (
              <>
                <div className="gen-explainer-controls-row">
                  <VoiceSelector voice={selectedVoice} onVoiceChange={setSelectedVoice} disabled={isLoading} playbackRate={playbackRate} language={languageCode} groupClassName="gen-explainer-form-group" labelClassName="gen-explainer-label" selectClassName="gen-explainer-select" sampleClassName="gen-explainer-voice-sample" sampleAudioClassName="gen-explainer-voice-sample-audio" />
                </div>
              </>
            )}

            {/* Background Music Selection */}
            <div className="gen-explainer-form-group">
              <label htmlFor="background-music-select" className="gen-explainer-label">
                Background Music
              </label>
              <select id="background-music-select" className="gen-explainer-select" value={backgroundMusic} onChange={(e) => setBackgroundMusic(e.target.value)}>
                <option value="">None</option>
                {musicOptions.map((music) => (
                  <option key={music.name} value={music.url}>{music.name}</option>
                ))}
              </select>
              {backgroundMusic && (
                <div className="gen-explainer-voice-sample">
                  <audio src={backgroundMusic} controls loop={true} controlsList="nodownload nofullscreen noplaybackrate" className="gen-explainer-voice-sample-audio" />
                </div>
              )}
            </div>
            {/* Disclaimer */}
            <div className="gen-explainer-form-group gen-explainer-checkbox-container">
              <input type="checkbox" id="enable_disclaimer" className="gen-explainer-checkbox" checked={showDisclaimer} onChange={(e) => setShowDisclaimer(e.target.checked)} disabled={selectedAudience == "patients"} title={selectedAudience == "patients" ? "Disclaimers are required for explainers targeted at patients." : ""} />
              <label htmlFor="enable_disclaimer" className="gen-explainer-checkbox-label">
                Add a disclaimer or additional information
              </label>
            </div>

            {showDisclaimer && (
              <div className="gen-explainer-form-group">
                <textarea dir="auto" id="disclaimer-text" className="gen-explainer-textarea" placeholder="Enter disclaimer text here..." value={disclaimer} onChange={(e) => { setDisclaimer(e.target.value); setDisclaimerTouched(true) }} />
              </div>
            )}

            {/* Audience Selection */}
            <div className="gen-explainer-form-group">
              <label htmlFor="audience-select" className="gen-explainer-label">
                Audience
              </label>
              <select id="audience-select" className="gen-explainer-select" value={selectedAudience} onChange={(e) => setSelectedAudience(e.target.value)}>
                {Object.keys(explainerAudiences).map((audience) => (
                  <option key={audience} value={audience} title={explainerAudiences[audience].description}>{explainerAudiences[audience].name}</option>
                ))}
              </select>
            </div>

            {/* Error Display */}
            {error && (
              <div style={{
                padding: '15px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                color: '#fca5a5',
                marginBottom: '20px'
              }}>
                <strong>Error:</strong> {error}
              </div>
            )}
            {brandingProfileList && brandingProfileList.length >= 2 ? (
              <div className="gen-explainer-form-group" id="branding-profile-select">
                <label htmlFor="branding-select" className="gen-explainer-label">
                  Branding Profile
                </label>
                <select id="branding-select" className="gen-explainer-select"
                  value={brandingProfileId}
                  onChange={(event) => {
                    setBrandingProfileId(event.target.value);
                  }}
                  disabled={isLoading}
                >
                  {brandingProfileList.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.profile_name} {e.suffix ?? ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {/* Generate Button */}
            <div className="gen-explainer-button-group">
              <button
                className="gen-explainer-button gen-explainer-button-primary"
                onClick={handleGenerateExplainer}
                disabled={isLoading || !query.trim()}
              >
                {isLoading ? (
                  <>
                    <span className="gen-explainer-spinner"></span>
                    Generating Explainer...
                  </>
                ) : (
                  'Generate Explainer'
                )}
              </button>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="gen-explainer-loading">
                <div className="gen-explainer-loading-message">
                  Creating interactive video...
                </div>
                <div className="gen-explainer-loading-submessage">
                  This can take a few minutes.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Explainer Preview Section */}
        {explainer && (
          <div className="gen-explainer-card">
            <div className="gen-explainer-preview">
              <div className="gen-explainer-preview-header">
                <h2>Your Explainer is Ready!</h2>
                <button
                  className="gen-explainer-button gen-explainer-button-secondary"
                  onClick={handleNewExplainer}
                  style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                >
                  Create New Explainer
                </button>
              </div>

              {/* Iframe Container with Close Button */}
              <div className="gen-explainer-iframe-container">
                {explainer.url ? (
                  <>
                    <button
                      className="gen-explainer-iframe-close"
                      onClick={handleCloseExplainer}
                      title="Close video"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <iframe
                      src={explainer.url}
                      id="gen-explainer-iframe-id"
                      className="gen-explainer-iframe"
                      title="Generated Explainer"
                      allow="autoplay; fullscreen"
                    />
                  </>
                ) : (
                  <div className="gen-explainer-iframe-placeholder">
                    <div className="gen-explainer-iframe-placeholder-icon">▶</div>
                    <div>Explainer is being prepared...</div>
                  </div>
                )}
              </div>

              {/* Link Display with Copy and Share Buttons */}
              {explainer.url && (
                <div className="gen-explainer-link-section">
                  <div className="gen-explainer-link-header">
                    <div className="gen-explainer-link-label">Explainer Link:</div>
                    <div className="gen-explainer-link-actions">
                      <button
                        className="gen-explainer-copy-button"
                        onClick={(e) => { handleCopyLink(); refocusOnIframe(e.target); }}
                        title="Copy link to clipboard"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                        <span>{linkCopied ? 'Copied!' : 'Copy'}</span>
                      </button>
                      <button
                        className="gen-explainer-share-button"
                        id="gen-explainer-share-button-id"
                        onClick={() => setShowShareModal(true)}
                        title="Share this explainer"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>
                  <a
                    href={explainer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gen-explainer-link"
                    onClick={(e) => { refocusOnIframe(e.target); }}
                  >
                    {explainer.url}
                  </a>
                </div>
              )}

              {/* Download Video Section */}
              {/* {explainer.url && (
                <div className="gen-explainer-download-section">
                  <h3>Download Video</h3>
                  <div className="gen-explainer-download-controls">
                    <div className="gen-explainer-form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label htmlFor="download-resolution" className="gen-explainer-label">
                        Video Resolution
                      </label>
                      <select
                        id="download-resolution"
                        className="gen-explainer-select"
                        value={downloadResolution}
                        onChange={(e) => setDownloadResolution(e.target.value)}
                        disabled={isDownloading}
                      >
                        <option value="480p">480p (SD)</option>
                        <option value="720p">720p (HD)</option>
                        <option value="1080p">1080p (Full HD) ⭐ Premium</option>
                        <option value="4k">4K (Ultra HD) ⭐ Premium</option>
                      </select>
                    </div>
                    <button
                      className="gen-explainer-button gen-explainer-button-download"
                      onClick={handleDownloadVideo}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <>
                          <span className="gen-explainer-spinner"></span>
                          Preparing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faDownload} />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  </div>
                  {(downloadResolution === '1080p' || downloadResolution === '4k') && (
                    <div className="gen-explainer-premium-notice">
                      <FontAwesomeIcon icon={faCrown} />
                      <span>High-resolution downloads require a premium account</span>
                    </div>
                  )}
                </div>
              )} */}

              {explainer.url && explainer.can_record && !window.isMobile && (
                <div className="gen-explainer-download-section">
                  <h3>Record Video</h3>
                  <div className="gen-explainer-download-controls">
                    <div className="gen-explainer-form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label htmlFor="record-resolution" className="gen-explainer-label">
                        Video Resolution & Format
                      </label>
                      <select id="record-resolution" className="gen-explainer-select" value={recordChoice} onChange={(e) => setRecordChoice(e.target.value)} >
                        {getVideoOptions().map((option, index) => (
                          <option key={`${option.width}x${option.height}-${option.mime}`} value={index}>{option.width}x{option.height} {option.mime.split("/")[1]}</option>
                        ))}
                      </select>
                    </div>
                    <button className="gen-explainer-button gen-explainer-button-primary" onClick={() => setIsRecording(true)} disabled={isRecording}>
                      {isRecording ? 'Recording...' : 'Record Video'}
                    </button>
                  </div>
                  {isRecording && <ExplainerRecorder show={true} onHide={() => setIsRecording(false)}
                    explainer={explainer} videoMime={getVideoOptions()?.[recordChoice]?.mime} videoWidth={getVideoOptions()?.[recordChoice]?.width}
                    videoHeight={getVideoOptions()?.[recordChoice]?.height} />}
                </div>
              )}

              {/* Feedback Section */}
              <div className="gen-explainer-feedback">
                <h3>Want to Modify This Explainer?</h3>
                <div className="gen-explainer-feedback-description">
                  Tell us what you'd like to change, add, or improve in this explainer, and we'll regenerate it based on your feedback.
                </div>
                <div className="gen-explainer-form-group">
                  <label htmlFor="explainer-feedback" className="gen-explainer-label">
                    Your Feedback
                    <span className="gen-explainer-label-description">
                      e.g., "Add more details about X" or "Make it slower" or "Focus more on Y"
                    </span>
                  </label>
                  <textarea
                    id="explainer-feedback"
                    className="gen-explainer-textarea"
                    placeholder="Describe what you'd like to change about this explainer..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    style={{ minHeight: '100px' }}
                  />
                </div>
                <div className="gen-explainer-button-group">
                  <button
                    className="gen-explainer-button gen-explainer-button-primary"
                    onClick={handleSubmitFeedback}
                    disabled={!feedback.trim()}
                    style={{ padding: '12px 24px', fontSize: '1rem' }}
                  >
                    Regenerate with Feedback
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {explainer && explainer.url && (
          <ShareModal
            show={showShareModal}
            onHide={() => {
              setShowShareModal(false);
              setTimeout(() => refocusOnIframe(document.getElementById("gen-explainer-share-button-id")), 200);
            }}
            url={explainer.url}
            qr={explainer.qr_image || ""}
            id={explainer.id || ""}
            isPresentation={false}
            canEmail={true}
          />
        )}
      </div>
    </div>
  );
}